import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { navigateTo } from '@helpers';
import { unlogin } from '@reducers/utils';
import { selectOnboardingWizardSubmitting, submitOnboardingWizard } from '@reducers/onboardingWizard';

import { STEP, SUBMITTED_HOLD_MS, type StepIndex } from '@components/tenantStatus/onboardingWizard/constants';
import type { WizardFormState } from '@components/tenantStatus/onboardingWizard/types';
import { buildPayload, getInitialForm } from '@components/tenantStatus/onboardingWizard/helpers';

export const useManageOnboardingWizard = () => {
  const dispatch = useAppDispatch();
  const submitting = useSelector(selectOnboardingWizardSubmitting);

  const [currentStep, setCurrentStep] = useState<StepIndex>(STEP.WELCOME);
  const [form, setForm] = useState<WizardFormState>(getInitialForm);
  const navigateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (navigateTimer.current) clearTimeout(navigateTimer.current);
    },
    []
  );

  const updateMachines = useCallback((next: WizardFormState['machines']) => {
    setForm((prev) => ({ ...prev, machines: next }));
  }, []);
  const updateErp = useCallback((next: WizardFormState['erp']) => {
    setForm((prev) => ({ ...prev, erp: next }));
  }, []);
  const updateGoals = useCallback((next: WizardFormState['goals']) => {
    setForm((prev) => ({ ...prev, goals: next }));
  }, []);
  const updateTeam = useCallback((next: WizardFormState['team']) => {
    setForm((prev) => ({ ...prev, team: next }));
  }, []);

  const onLogOut = useCallback(() => {
    dispatch(unlogin());
  }, [dispatch]);

  const onBack = useCallback(() => {
    setCurrentStep((prev) => (prev > STEP.WELCOME ? ((prev - 1) as StepIndex) : prev));
  }, []);

  const onNext = useCallback(async () => {
    if (currentStep === STEP.TEAM) {
      const succeeded = await dispatch(submitOnboardingWizard(buildPayload(form)));
      if (!succeeded) return;
      setCurrentStep(STEP.SUBMITTED);
      navigateTimer.current = setTimeout(() => {
        // userProfileDetails returns 200, so the 403 pipeline can't redirect — navigate
        // explicitly so the next 403 lands on OnHold (not the wizard again).
        navigateTo('OnHold');
      }, SUBMITTED_HOLD_MS);
      return;
    }
    setCurrentStep((prev) => (prev + 1) as StepIndex);
  }, [currentStep, dispatch, form]);

  const onGetStarted = useCallback(() => {
    setCurrentStep(STEP.MACHINES);
  }, []);

  return {
    currentStep,
    form,
    submitting,
    updateMachines,
    updateErp,
    updateGoals,
    updateTeam,
    onLogOut,
    onBack,
    onNext,
    onGetStarted,
  };
};
